import { useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { DataTablePagination } from '@/components/data-table/Pagination';
import { useGetUserClubHistory } from '@/services';
import { formatDateWithDifference } from '@/utils/transformer';
import { cn } from '@/lib/utils';

const SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
	ACTIVE: 'Ativa',
	PENDING: 'Pendente',
	CANCELLED: 'Cancelada',
	EXPIRED: 'Expirada',
	SUSPENDED: 'Suspensa',
};

const TIMELINE_ACTION_LABEL: Record<string, string> = {
	BENEFITS_SYNCED: 'Benefícios sincronizados',
	BENEFITS_RESET: 'Benefícios renovados',
	USAGE_UPDATED: 'Uso alterado manualmente',
	BENEFIT_APPLIED_TO_SESSION: 'Benefício usado em sessão',
	BENEFIT_RESTORED: 'Benefício restaurado',
};

const TIMELINE_SOURCE_LABEL: Record<Clubs.ClubTimelineSource, string> = {
	subscription: 'Assinatura',
	benefit: 'Benefício',
};

const TIMELINE_ROLE_LABEL: Record<string, string> = {
	admin: 'Admin',
	system: 'Sistema',
	session_payment: 'Pagamento de sessão',
};

const SOURCE_FILTER_ALL = '__all__';
const SUBSCRIPTION_FILTER_ALL = '__all__';

const TIMELINE_PAGE_SIZE = 20;

type UserClubSubscription = NonNullable<
	Users.User['user_club_subscription']
>[number];

interface UserClubTimelineProps {
	userId: string;
	subscriptions: UserClubSubscription[];
	clubById: Record<string, Clubs.Club | undefined>;
}

function formatSubscriptionOptionLabel(
	sub: UserClubSubscription,
	clubById: Record<string, Clubs.Club | undefined>,
): string {
	const clubName = sub.club?.name ?? clubById[sub.club_id]?.name ?? 'Clube';
	const statusLabel = SUBSCRIPTION_STATUS_LABEL[sub.status] ?? sub.status;
	return `${clubName} · ${statusLabel} · ${sub.id.slice(0, 8)}…`;
}

function getActionLabel(action: string): string {
	return TIMELINE_ACTION_LABEL[action] ?? action;
}

function getRoleLabel(role: string): string {
	return TIMELINE_ROLE_LABEL[role] ?? role;
}

const UserClubTimeline = ({
	userId,
	subscriptions,
	clubById,
}: UserClubTimelineProps) => {
	const [page, setPage] = useState(1);
	const [sourceFilter, setSourceFilter] = useState(SOURCE_FILTER_ALL);
	const [subscriptionFilter, setSubscriptionFilter] = useState(
		SUBSCRIPTION_FILTER_ALL,
	);

	const historyParams: Clubs.GetUserClubHistoryParams = {
		page,
		limit: TIMELINE_PAGE_SIZE,
		...(sourceFilter !== SOURCE_FILTER_ALL
			? { source: sourceFilter as Clubs.ClubTimelineSource }
			: {}),
		...(subscriptionFilter !== SUBSCRIPTION_FILTER_ALL
			? { subscriptionId: subscriptionFilter }
			: {}),
	};

	const { data, isLoading, isError, isFetching } = useGetUserClubHistory(
		userId,
		historyParams,
	);

	useEffect(() => {
		setPage(1);
		setSourceFilter(SOURCE_FILTER_ALL);
		setSubscriptionFilter(SUBSCRIPTION_FILTER_ALL);
	}, [userId]);

	useEffect(() => {
		setPage(1);
	}, [sourceFilter, subscriptionFilter]);

	const events = data?.data ?? [];
	const totalItems = data?.pagination?.total ?? 0;

	return (
		<div className="space-y-4 rounded-2xl border border-zinc-100 bg-zinc-50/40 p-4 md:p-5">
			<div className="flex items-start gap-2">
				<div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-cyan-100 bg-cyan-50 text-cyan-800">
					<History className="size-4" aria-hidden />
				</div>
				<div>
					<h3 className="text-sm font-bold text-zinc-900">
						Linha do tempo do clube
					</h3>
					<p className="text-xs text-zinc-500">
						Eventos de assinatura e benefícios, do mais recente ao
						mais antigo.
					</p>
				</div>
			</div>

			<div className="grid gap-3 sm:grid-cols-2">
				<div>
					<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">
						Tipo de evento
					</span>
					<Select
						value={sourceFilter}
						onValueChange={setSourceFilter}
					>
						<SelectTrigger className="h-10 w-full border-zinc-200 bg-white text-left text-sm">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={SOURCE_FILTER_ALL}>
								Todos
							</SelectItem>
							<SelectItem value="subscription">
								Assinatura
							</SelectItem>
							<SelectItem value="benefit">Benefício</SelectItem>
						</SelectContent>
					</Select>
				</div>

				{subscriptions.length > 0 ? (
					<div>
						<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">
							Assinatura
						</span>
						<Select
							value={subscriptionFilter}
							onValueChange={setSubscriptionFilter}
						>
							<SelectTrigger className="h-10 w-full border-zinc-200 bg-white text-left text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={SUBSCRIPTION_FILTER_ALL}>
									Todas as assinaturas
								</SelectItem>
								{subscriptions.map((sub) => (
									<SelectItem key={sub.id} value={sub.id}>
										{formatSubscriptionOptionLabel(
											sub,
											clubById,
										)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : null}
			</div>

			{isLoading || isFetching ? (
				<div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-200 bg-white px-4 py-10 text-sm text-zinc-500">
					<Loader2 className="size-4 animate-spin" aria-hidden />
					Carregando histórico…
				</div>
			) : isError ? (
				<p className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-6 text-center text-sm text-rose-800">
					Não foi possível carregar a linha do tempo.
				</p>
			) : events.length === 0 ? (
				<p className="rounded-xl border border-dashed border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">
					Nenhum evento encontrado para os filtros selecionados.
				</p>
			) : (
				<>
					<ul className="space-y-3">
						{events.map((event) => {
							const sourceLabel =
								TIMELINE_SOURCE_LABEL[event.source] ??
								event.source;
							const isBenefit = event.source === 'benefit';

							return (
								<li
									key={event.id}
									className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm"
								>
									<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
										<div className="min-w-0 space-y-2">
											<div className="flex flex-wrap items-center gap-2">
												<span
													className={cn(
														'inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide',
														isBenefit
															? 'bg-purple-100 text-purple-800'
															: 'bg-cyan-100 text-cyan-900',
													)}
												>
													{sourceLabel}
												</span>
												<span className="text-sm font-bold text-zinc-900">
													{getActionLabel(
														event.action,
													)}
												</span>
											</div>
											{event.description?.trim() ? (
												<p className="text-sm leading-relaxed text-zinc-600">
													{event.description}
												</p>
											) : null}
											<p className="text-[11px] text-zinc-400">
												Ator:{' '}
												<span className="font-medium text-zinc-600">
													{getRoleLabel(
														event.triggered_by_role,
													)}
												</span>
												<span className="mx-1">·</span>
												<span className="font-mono text-[10px]">
													{event.triggered_by}
												</span>
											</p>
										</div>
										<div className="shrink-0 text-right">
											<p className="text-xs font-medium text-zinc-700">
												{formatDateWithDifference(
													event.triggered_at,
												)}
											</p>
											<p className="mt-0.5 font-mono text-[10px] text-zinc-400">
												{event.action}
											</p>
										</div>
									</div>
								</li>
							);
						})}
					</ul>

					{totalItems > TIMELINE_PAGE_SIZE ? (
						<div className="flex items-center justify-between border-t border-zinc-200/80 pt-3">
							<p className="text-xs text-zinc-500">
								{totalItems} evento{totalItems === 1 ? '' : 's'}
							</p>
							<DataTablePagination
								page={page}
								pageSize={TIMELINE_PAGE_SIZE}
								totalItems={totalItems}
								onPageChange={setPage}
							/>
						</div>
					) : null}
				</>
			)}
		</div>
	);
};

export { UserClubTimeline };
